import { useEffect, useRef } from "react";

const STABLE_FRAMES = 3;
const SETTLE_TIMEOUT_MS = 2500;

export const useScrollRestore = ({ targetY, enabled, onDone }) => {
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const report = () => {
    if (typeof onDoneRef.current === "function") onDoneRef.current();
  };

  useEffect(() => {
    if (!enabled || doneRef.current) return undefined;
    if (targetY == null || targetY <= 0) {
      doneRef.current = true;
      report();
      return undefined;
    }

    let frameId = null;
    let aborted = false;
    let lastHeight = -1;
    let stableCount = 0;
    const deadline = Date.now() + SETTLE_TIMEOUT_MS;

    const previousRestoration = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = "manual";
    } catch (err) {
      /* not supported */
    }

    const abort = () => {
      aborted = true;
      doneRef.current = true;
      report();
    };

    const stop = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("wheel", abort);
      window.removeEventListener("touchstart", abort);
      window.removeEventListener("keydown", abort);
      try {
        window.history.scrollRestoration = previousRestoration || "auto";
      } catch (err) {
        /* not supported */
      }
    };

    const applyScroll = () => {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({ top: Math.min(targetY, maxScroll), behavior: "auto" });
      doneRef.current = true;
      report();
    };

    const tick = () => {
      if (aborted) {
        stop();
        return;
      }

      const height = document.documentElement.scrollHeight;
      if (height === lastHeight) {
        stableCount += 1;
      } else {
        lastHeight = height;
        stableCount = 0;
      }

      if (stableCount >= STABLE_FRAMES || Date.now() >= deadline) {
        applyScroll();
        stop();
        return;
      }

      frameId = window.requestAnimationFrame(tick);
    };

    window.addEventListener("wheel", abort, { passive: true });
    window.addEventListener("touchstart", abort, { passive: true });
    window.addEventListener("keydown", abort);
    frameId = window.requestAnimationFrame(tick);

    return stop;
  }, [enabled, targetY]);
};
